import React from 'react';
import {
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import type { LabourItemDiff, ChangeType } from '../../../types/reimportTypes';

interface ReimportLabourItemRowProps {
  item: LabourItemDiff;
}

const changeTypeConfig: Record<
  ChangeType,
  { label: string; color: 'success' | 'error' | 'warning' | 'default' }
> = {
  Added: { label: '+ item added', color: 'success' },
  Removed: { label: '- item removed', color: 'error' },
  Modified: { label: '~ price changed', color: 'warning' },
  Unchanged: { label: '= unchanged', color: 'default' },
};

const getPriceChangeLabel = (
  item: LabourItemDiff
): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
  if (
    item.changeType === 'Modified' &&
    item.currentPrice !== null &&
    item.newPrice !== null
  ) {
    if (item.newPrice > item.currentPrice) {
      return { label: '~ price increased', color: 'warning' };
    }
    return { label: '~ price decreased', color: 'warning' };
  }
  return changeTypeConfig[item.changeType];
};

const formatPrice = (price: number | null): string => {
  if (price === null) return '-';
  return `£${price.toFixed(2)}`;
};

const ReimportLabourItemRow: React.FC<ReimportLabourItemRowProps> = ({
  item,
}) => {
  const config = getPriceChangeLabel(item);

  return (
    <TableRow
      sx={{
        bgcolor:
          item.changeType === 'Added'
            ? 'success.50'
            : item.changeType === 'Removed'
              ? 'error.50'
              : item.changeType === 'Modified'
                ? 'warning.50'
                : undefined,
      }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{item.definitionName}</Typography>
          {item.isProtected && (
            <Tooltip
              title={`Protected: ${item.activeAllocationCount} active allocation(s), £${item.totalValueBooked.toFixed(2)} booked`}
            >
              <LockIcon fontSize="small" color="action" />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Chip label={config.label} color={config.color} size="small" />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">
          {formatPrice(item.currentPrice)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatPrice(item.newPrice)}</Typography>
      </TableCell>
    </TableRow>
  );
};

export default ReimportLabourItemRow;
